import * as THREE from "three";
import { Engine } from "../../game/engine";
import { AudioBed } from "../../game/audio";

/**
 * Spire — precision-stack, redesigned for 3D depth + engagement loop.
 * Game 09 of 12. (v2 — pilot for the dossier-wide "feel-pass" sweep.)
 *
 * Scoring contract preserved: server replays sweepX(block, t_ms) against
 * each drop_x, so any change to that algorithm or the block schedule
 * would break the anti-cheat. Everything else — camera, lighting,
 * geometry, audio bed, escalation, surprise events — is free to evolve.
 */

export const ARENA_HALF = 20;
export const W0 = 2.0;
export const PERFECT_TOL = 0.05;
export const PERFECT_TOL_MAGNETIC = 0.12;
export const MAX_BLOCKS = 200;
const SPEED_MIN = 6;
const SPEED_MAX = 20;
const GUST_SPEED_MULT = 2.5;

/** v5(a) — milestone thresholds (mirrors scoring-spire MILESTONES). Each
 *  milestone tweens the sky+fog palette toward its target and triggers a
 *  held drone swell on the shared setChimeGain audio channel. Palette
 *  named after the dossier zones: clouds=white, stratosphere=cyan,
 *  space=violet, moon=gold. Kept dark enough that bloom doesn't wash out
 *  the blocks; the tint is what carries the read. */
const SKY_BASE = 0x05060a;
const FOG_BASE = 0x05060a;
const MILESTONES_CLIENT: { blocks: number; name: string; bonus: number; sky: number; fog: number }[] = [
  { blocks: 25,  name: "Clouds",       bonus: 1000,  sky: 0x5a6678, fog: 0x4a5868 }, // pale white-gray
  { blocks: 50,  name: "Stratosphere", bonus: 2500,  sky: 0x0a3050, fog: 0x0e3c60 }, // cyan-shifted
  { blocks: 100, name: "Space",        bonus: 5000,  sky: 0x180a32, fog: 0x1c0e3a }, // violet
  { blocks: 200, name: "Moon",         bonus: 12000, sky: 0x2a2008, fog: 0x342812 }, // gold-warm
];
const BLOCK_HEIGHT = 1.0;
const BLOCK_DEPTH = 2.4;

export type Side = "L" | "R";
export type BlockKind = "normal" | "magnetic" | "gust" | "perfect_only";

/** Mirror of scoring-spire.ts blockKind — must stay in lockstep so the
 *  server can replay the exact same kind for each idx. */
function blockKind(idx: number): BlockKind {
  if (idx >= 17 && idx % 17 === 0) return "perfect_only";
  if (idx >= 13 && idx % 13 === 0) return "gust";
  if (idx >= 7 && idx % 7 === 0) return "magnetic";
  return "normal";
}

export interface DropEvent { t_ms: number; drop_x: number; rescued?: boolean }

export interface SpireCallbacks {
  /** Frame HUD update: height, perfects, score, current/next-block kind. */
  onHud: (state: {
    height: number;
    perfects: number;
    scoreEstimate: number;
    currentKind: BlockKind;
    nextKind: BlockKind;
    rescueAvailable: boolean;
  }) => void;
  /** Fired when the tower would fall on the current drop AND rescue is
   *  still available. The bootstrap shows a "Rescue?" prompt; the player
   *  decides within ~2.5s whether to use it. Resolve(true) consumes the
   *  rescue and the block snaps to the previous center; resolve(false)
   *  lets the run end. */
  onRescuePrompt: (resolve: (use: boolean) => void) => void;
  onGameOver: (result: {
    drops: DropEvent[];
    scoreEstimate: number;
    perfects: number;
    height: number;
    rescued: boolean;
    milestonesReached: number;
    /** v5(c) — per-kind recap. `faced` = blocks the player tapped on
     *  (including a tower-fall tap); `perfect` = of those, how many
     *  landed within PERFECT_TOL. Indexed by BlockKind. */
    kindFaced: Record<BlockKind, number>;
    kindPerfect: Record<BlockKind, number>;
  }) => void;
  /** v4 — fires once when block_count crosses a milestone threshold
   *  (25/50/100/200). Bootstrap renders a banner. */
  onMilestone?: (info: { blocks: number; name: string; bonus: number }) => void;
}

interface Block { idx: number; side: Side; enter_x: number; speed_xps: number; kind: BlockKind; }

interface StackedBlock {
  mesh: THREE.Mesh;
  width: number;
  perfect: boolean;
  aurora: boolean;
  /** Per-frame lean contribution this block adds to the tower sway. */
  leanContrib: number;
  /** Bounce settle progress 0..1; eases the drop animation. */
  settle: number;
  /** Target Y the block animates toward. */
  targetY: number;
}

function makeRng(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function generateBlocks(seed: number): Block[] {
  const rng = makeRng(seed);
  const out: Block[] = [];
  for (let i = 0; i < MAX_BLOCKS; i++) {
    const side: Side = rng() < 0.5 ? "L" : "R";
    const enter_x = side === "L" ? -ARENA_HALF : ARENA_HALF;
    let speed_xps = SPEED_MIN + rng() * (SPEED_MAX - SPEED_MIN);
    const kind = blockKind(i);
    if (kind === "gust") speed_xps *= GUST_SPEED_MULT;
    out.push({ idx: i, side, enter_x, speed_xps, kind });
  }
  return out;
}

function sweepX(block: Block, tMs: number): number {
  const t = tMs / 1000;
  const dir = block.side === "L" ? 1 : -1;
  const raw = block.enter_x + dir * block.speed_xps * t;
  const period = 4 * ARENA_HALF;
  const shifted = raw + ARENA_HALF;
  const m = ((shifted % period) + period) % period;
  const tri = m <= 2 * ARENA_HALF ? m : period - m;
  return tri - ARENA_HALF;
}

/** Smoothstep ease for settle animation — feels like a soft slam. */
function ease(t: number): number {
  const x = Math.max(0, Math.min(1, t));
  return x * x * (3 - 2 * x);
}

export class Spire {
  private engine: Engine;
  private audio = new AudioBed();
  private blocks: Block[];
  private currentMesh: THREE.Mesh;
  private currentMat: THREE.MeshStandardMaterial;
  private currentGeo: THREE.BoxGeometry;
  private ghostBeam: THREE.Mesh;
  private topLeft = -ARENA_HALF;
  private topRight = ARENA_HALF;
  private blockIdx = 0;
  private milestonesReached = 0;
  private blockStartedAt = 0;
  private drops: DropEvent[] = [];
  private scoreEstimate = 0;
  private perfects = 0;
  private gameOver = false;
  private settling = false;
  private cb: SpireCallbacks;
  private canvas: HTMLCanvasElement;
  private cameraTargetY = 0;
  private cameraY = 0;
  private cameraTargetZ = 28;
  private cameraZ = 28;
  private stackHeight = 0;
  private stack: StackedBlock[] = [];
  private rescueUsed = false;
  private floorMesh: THREE.Mesh;
  private gustParticles: THREE.Points;
  private gustActive = 0;
  /** v5(b) — "catch the wind" pre-spawn cue for gust blocks. Set when
   *  the upcoming block (blockIdx, post-settle increment) is a gust;
   *  the frame loop fades a directional arrow in on the entry side
   *  for the last ~300ms before the block enters. */
  private gustWarnSide: Side | null = null;
  private gustWarnRemaining = 0;
  private gustArrow: THREE.Mesh;
  private gustArrowMat: THREE.MeshBasicMaterial;
  /** v5(c) — per-kind recap counters. Incremented client-side as the
   *  player taps each block; surfaced on game-over for the histogram. */
  private kindFaced: Record<BlockKind, number> = { normal: 0, magnetic: 0, gust: 0, perfect_only: 0 };
  private kindPerfect: Record<BlockKind, number> = { normal: 0, magnetic: 0, gust: 0, perfect_only: 0 };
  private bedIntensity = 0;
  /** Cumulative tower lean angle (radians). Visualized as a tilt of the stack root. */
  private leanAngle = 0;
  private stackRoot: THREE.Group;
  /** v5(a) — milestone palette tween state. `current*` are the live
   *  colors applied each frame to scene.background and fog.color;
   *  `target*` are the milestone destination. Linear-lerped at ~1.2/s. */
  private currentSky = new THREE.Color(SKY_BASE);
  private targetSky = new THREE.Color(SKY_BASE);
  private currentFog = new THREE.Color(FOG_BASE);
  private targetFog = new THREE.Color(FOG_BASE);
  /** Audio swell decay: when > 0, setChimeGain is ramping back to 0. */
  private chimeSwellRemaining = 0;

  private onTap = () => this.handleDrop();
  private onKey = (e: KeyboardEvent) => {
    if (e.code === "Space" || e.code === "Enter") {
      e.preventDefault();
      this.handleDrop();
    }
  };

  constructor(canvas: HTMLCanvasElement, cb: SpireCallbacks, seed = Math.floor(Math.random() * 0x7fffffff)) {
    this.canvas = canvas;
    this.cb = cb;
    this.blocks = generateBlocks(seed);

    this.engine = new Engine(canvas, {
      onFrame: (dt, t) => this.frame(dt, t),
    });

    // Perspective camera angled down at the stack. Lifts and dollies
    // back as the stack grows so the player can always see the top
    // ~6 blocks plus a glimpse of the base.
    this.engine.camera.position.set(0, 3, 28);
    this.engine.camera.lookAt(0, 0, 0);
    this.engine.camera.fov = 55;
    this.engine.camera.updateProjectionMatrix();

    // Real lighting so the box blocks have shading depth.
    const ambient = new THREE.AmbientLight(0x6080a0, 0.35);
    this.engine.scene.add(ambient);
    const key = new THREE.DirectionalLight(0xfff0d0, 1.15);
    key.position.set(8, 16, 9);
    this.engine.scene.add(key);
    const fill = new THREE.DirectionalLight(0x4060ff, 0.4);
    fill.position.set(-10, 4, 6);
    this.engine.scene.add(fill);

    // The stack lives under a Group so cumulative tower lean is one rotation.
    this.stackRoot = new THREE.Group();
    this.engine.scene.add(this.stackRoot);

    // v5(a): own the sky background so milestone palette tweens have
    // something to write to (engine only sets renderer.clearColor).
    // Fog is a FogExp2 set by engine — we mutate its .color, never its density.
    this.engine.scene.background = this.currentSky.clone();

    // Vertical glow rails at ±ARENA_HALF — gives a "channel" read.
    for (const side of [-1, 1]) {
      const railGeo = new THREE.BoxGeometry(0.18, 200, 0.18);
      const railMat = new THREE.MeshBasicMaterial({
        color: 0xff5b3a,
        transparent: true,
        opacity: 0.5,
        blending: THREE.AdditiveBlending,
      });
      const rail = new THREE.Mesh(railGeo, railMat);
      rail.position.set(side * ARENA_HALF, 30, -1.5);
      this.engine.scene.add(rail);
    }

    // Floor — a wide dark disc with rim glow. Anchors the scene visually.
    const floorGeo = new THREE.CircleGeometry(60, 80);
    const floorMat = new THREE.MeshStandardMaterial({
      color: 0x06080f,
      roughness: 0.85,
      metalness: 0.15,
    });
    this.floorMesh = new THREE.Mesh(floorGeo, floorMat);
    this.floorMesh.rotation.x = -Math.PI / 2;
    this.floorMesh.position.y = -0.5;
    this.engine.scene.add(this.floorMesh);

    const floorRimGeo = new THREE.RingGeometry(59.4, 60.6, 80);
    const floorRimMat = new THREE.MeshBasicMaterial({
      color: 0x66ddff,
      transparent: true,
      opacity: 0.4,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    });
    const floorRim = new THREE.Mesh(floorRimGeo, floorRimMat);
    floorRim.rotation.x = -Math.PI / 2;
    floorRim.position.y = -0.49;
    this.engine.scene.add(floorRim);

    // Current sweeping block — box with emissive surface.
    this.currentGeo = new THREE.BoxGeometry(W0, BLOCK_HEIGHT, BLOCK_DEPTH);
    this.currentMat = new THREE.MeshStandardMaterial({
      color: 0x00b0ff,
      emissive: 0x0080ff,
      emissiveIntensity: 0.7,
      roughness: 0.4,
      metalness: 0.3,
    });
    this.currentMesh = new THREE.Mesh(this.currentGeo, this.currentMat);
    this.engine.scene.add(this.currentMesh);

    // Ghost-drop beam — a faint vertical bar showing where the block
    // would land. Gives the player a much clearer aim cue than a 2D
    // top-down view ever could.
    const ghostGeo = new THREE.BoxGeometry(0.08, 200, 0.08);
    const ghostMat = new THREE.MeshBasicMaterial({
      color: 0x46ffe0,
      transparent: true,
      opacity: 0.18,
      blending: THREE.AdditiveBlending,
    });
    this.ghostBeam = new THREE.Mesh(ghostGeo, ghostMat);
    this.ghostBeam.position.set(0, 100, 0);
    this.engine.scene.add(this.ghostBeam);

    // Gust particles — pre-allocated cloud, position-keyed for the event.
    const gustCount = 220;
    const gustPositions = new Float32Array(gustCount * 3);
    for (let i = 0; i < gustCount; i++) {
      gustPositions[i * 3 + 0] = (Math.random() - 0.5) * 40;
      gustPositions[i * 3 + 1] = (Math.random() - 0.5) * 16;
      gustPositions[i * 3 + 2] = (Math.random() - 0.5) * 6;
    }
    const gustGeo = new THREE.BufferGeometry();
    gustGeo.setAttribute("position", new THREE.BufferAttribute(gustPositions, 3));
    const gustMat = new THREE.PointsMaterial({
      color: 0xa0e0ff,
      size: 0.12,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    this.gustParticles = new THREE.Points(gustGeo, gustMat);
    this.engine.scene.add(this.gustParticles);

    // v5(b): pre-spawn arrow for gust blocks. A flat triangle, additive
    // gold blend, hidden by default. Positioned + flipped by side each
    // time it arms; pulsed-fade via the frame loop. Pre-allocated so
    // the spawn moment doesn't pay for geometry construction.
    const arrowShape = new THREE.Shape();
    arrowShape.moveTo(0, 0);          // tip
    arrowShape.lineTo(-2.2, 1.2);
    arrowShape.lineTo(-1.6, 0);
    arrowShape.lineTo(-2.2, -1.2);
    arrowShape.closePath();
    const arrowGeo = new THREE.ShapeGeometry(arrowShape);
    this.gustArrowMat = new THREE.MeshBasicMaterial({
      color: 0xffd54a,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    this.gustArrow = new THREE.Mesh(arrowGeo, this.gustArrowMat);
    this.gustArrow.visible = false;
    this.engine.scene.add(this.gustArrow);

    canvas.addEventListener("pointerdown", this.onTap);
    window.addEventListener("keydown", this.onKey);
  }

  start() {
    this.audio.arm();
    this.topLeft = -ARENA_HALF;
    this.topRight = ARENA_HALF;
    this.blockIdx = 0;
    this.blockStartedAt = performance.now();
    this.drops = [];
    this.scoreEstimate = 0;
    this.perfects = 0;
    this.gameOver = false;
    this.settling = false;
    this.stackHeight = 0;
    this.cameraTargetY = 3;
    this.cameraY = 3;
    this.cameraTargetZ = 28;
    this.cameraZ = 28;
    this.bedIntensity = 0;
    this.leanAngle = 0;
    for (const b of this.stack) {
      this.stackRoot.remove(b.mesh);
      b.mesh.geometry.dispose();
      (b.mesh.material as THREE.Material).dispose();
    }
    this.stack = [];
    this.stackRoot.rotation.z = 0;
    this.rescueUsed = false;
    this.milestonesReached = 0;
    // v5(a): reset palette to base on every run so a Try-again from the
    // moon doesn't strand the sky in gold.
    this.currentSky.setHex(SKY_BASE);
    this.targetSky.setHex(SKY_BASE);
    this.currentFog.setHex(FOG_BASE);
    this.targetFog.setHex(FOG_BASE);
    if (this.engine.scene.background instanceof THREE.Color) {
      this.engine.scene.background.copy(this.currentSky);
    }
    if (this.engine.scene.fog && "color" in this.engine.scene.fog) {
      (this.engine.scene.fog as THREE.FogBase).color.copy(this.currentFog);
    }
    this.chimeSwellRemaining = 0;
    this.audio.setChimeGain(0, 0);
    this.gustWarnSide = null;
    this.gustWarnRemaining = 0;
    this.gustArrow.visible = false;
    this.gustArrowMat.opacity = 0;
    this.kindFaced = { normal: 0, magnetic: 0, gust: 0, perfect_only: 0 };
    this.kindPerfect = { normal: 0, magnetic: 0, gust: 0, perfect_only: 0 };
    this.refreshCurrentMesh();
    this.engine.start();
  }

  private refreshCurrentMesh() {
    const blockW = this.blockIdx === 0 ? W0 : (this.topRight - this.topLeft);
    const kind: BlockKind = this.blockIdx < this.blocks.length ? this.blocks[this.blockIdx].kind : "normal";
    this.currentMesh.geometry.dispose();
    this.currentMesh.geometry = new THREE.BoxGeometry(blockW, BLOCK_HEIGHT, BLOCK_DEPTH);
    if (kind === "perfect_only") {
      this.currentMat.color.setHex(0xff3060);
      this.currentMat.emissive.setHex(0xff1030);
      this.currentMat.emissiveIntensity = 1.8;
    } else if (kind === "gust") {
      this.currentMat.color.setHex(0xffd54a);
      this.currentMat.emissive.setHex(0xff8800);
      this.currentMat.emissiveIntensity = 1.6;
    } else if (kind === "magnetic") {
      this.currentMat.color.setHex(0x9a72ff);
      this.currentMat.emissive.setHex(0x4030cc);
      this.currentMat.emissiveIntensity = 1.2;
    } else {
      this.currentMat.color.setHex(0x00b0ff);
      this.currentMat.emissive.setHex(0x0080ff);
      this.currentMat.emissiveIntensity = 0.7;
    }
  }

  private handleDrop() {
    if (this.gameOver || this.settling) return;
    if (this.blockIdx >= this.blocks.length) return;
    const block = this.blocks[this.blockIdx];
    const kind = block.kind;
    // v5(c): count every tap as a "faced" block — including a topple
    // tap. Per-kind perfect counts are tracked at the perfect-landing
    // moment inside applyDrop. (Rescued drops don't bump the perfect
    // count here; they bypass kind-based perfect tolerance.)
    this.kindFaced[kind] += 1;
    const t = performance.now() - this.blockStartedAt;
    const x = sweepX(block, t);

    const blockW = this.blockIdx === 0 ? W0 : (this.topRight - this.topLeft);
    const footL = x - blockW / 2;
    const footR = x + blockW / 2;
    const newL = Math.max(this.topLeft, footL);
    const newR = Math.min(this.topRight, footR);

    const tol = kind === "magnetic" ? PERFECT_TOL_MAGNETIC : PERFECT_TOL;
    const prevCenter = (this.topLeft + this.topRight) / 2;
    const isPerfect = this.blockIdx > 0 && Math.abs(x - prevCenter) < tol;
    const wouldFall = newR - newL <= 0;
    const perfectOnlyMiss = kind === "perfect_only" && this.blockIdx > 0 && !isPerfect;

    if (wouldFall || perfectOnlyMiss) {
      // Offer a rescue if one is still in reserve.
      if (!this.rescueUsed) {
        this.settling = true;
        this.cb.onRescuePrompt((useRescue) => {
          if (useRescue) {
            this.rescueUsed = true;
            this.applyDrop(true, x, t, kind, block);
          } else {
            this.drops.push({ t_ms: Math.round(t), drop_x: Math.round(x * 100) / 100 });
            this.audio.thud();
            this.gameOver = true;
            this.collapseTower();
          }
        });
        return;
      }
      this.drops.push({ t_ms: Math.round(t), drop_x: Math.round(x * 100) / 100 });
      this.audio.thud();
      this.gameOver = true;
      this.collapseTower();
      return;
    }

    this.applyDrop(false, x, t, kind, block);
  }

  private applyDrop(rescued: boolean, x: number, t: number, kind: BlockKind, _block: Block) {
    const tol = kind === "magnetic" ? PERFECT_TOL_MAGNETIC : PERFECT_TOL;
    const prevCenter = (this.topLeft + this.topRight) / 2;

    // Rescued drops snap to perfect center (validator overrides drop_x).
    const effectiveX = rescued ? prevCenter : x;

    this.drops.push({
      t_ms: Math.round(t),
      drop_x: Math.round(x * 100) / 100,
      ...(rescued ? { rescued: true } : {}),
    });

    const blockW = this.blockIdx === 0 ? W0 : (this.topRight - this.topLeft);
    const footL = effectiveX - blockW / 2;
    const footR = effectiveX + blockW / 2;
    const newL = Math.max(this.topLeft, footL);
    const newR = Math.min(this.topRight, footR);
    const width = newR - newL;
    const center = (newL + newR) / 2;
    const isPerfect = (this.blockIdx > 0 && Math.abs(effectiveX - prevCenter) < tol) || rescued;

    let blockScore = Math.round(100 + 50 * width);
    if (isPerfect && !rescued) {
      this.perfects += 1;
      this.kindPerfect[kind] += 1;
      blockScore += kind === "perfect_only" ? 500 : kind === "gust" ? 400 : 200;
      this.audio.chime(kind === "perfect_only" ? 19 : kind === "gust" ? 14 : 12, 0);
    } else if (rescued) {
      blockScore -= 300;
      this.audio.chime(-5, 0);
    } else {
      this.audio.chime(kind === "gust" ? 7 : 4, 0);
    }
    if (rescued) {
      // Track rescue separately so the run-end card can show it.
      this.perfects += 1; // rescue snaps to perfect by definition
    }
    this.scoreEstimate = Math.max(0, this.scoreEstimate + blockScore);

    // Settle the dropped block into the stack.
    this.settling = true;
    const stackGeo = new THREE.BoxGeometry(width, BLOCK_HEIGHT, BLOCK_DEPTH);
    const baseColor = rescued
      ? 0x46ffe0
      : kind === "perfect_only" ? 0xff3060
      : kind === "gust" ? 0xffd54a
      : kind === "magnetic" ? 0x9a72ff
      : isPerfect ? 0xff5b3a : 0x00b0ff;
    const emissive = rescued
      ? 0x00aa90
      : kind === "perfect_only" ? 0xaa0040
      : kind === "gust" ? 0xff8800
      : kind === "magnetic" ? 0x4030cc
      : isPerfect ? 0xff3060 : 0x002080;
    const stackMat = new THREE.MeshStandardMaterial({
      color: baseColor,
      emissive,
      emissiveIntensity: rescued ? 1.4 : isPerfect ? 1.2 : 0.5,
      roughness: 0.35,
      metalness: 0.4,
    });
    const stackMesh = new THREE.Mesh(stackGeo, stackMat);
    const startY = this.stackHeight + 12;
    stackMesh.position.set(center, startY, 0);
    this.stackRoot.add(stackMesh);

    // Cumulative tower lean: perfect drops contribute zero; rescued drops zero.
    const leanContrib = isPerfect || rescued
      ? 0
      : (kind === "gust" ? 1.4 : 1) * (effectiveX - prevCenter) * 0.0035;

    this.stack.push({
      mesh: stackMesh,
      width,
      perfect: isPerfect,
      aurora: kind === "gust",
      leanContrib,
      settle: 0,
      targetY: this.stackHeight,
    });

    this.stackHeight += BLOCK_HEIGHT;
    this.topLeft = newL;
    this.topRight = newR;
    this.blockIdx += 1;
    // v5(a): milestone — fire callback, set palette tween target, kick
    // off held drone swell via setChimeGain. Note we set TARGETS only;
    // the frame loop lerps current→target so the shift is a swell, not
    // a hard cut. The frame loop also tapers the chime swell back to 0
    // over ~3.5s so the next milestone has somewhere to climb from.
    for (const m of MILESTONES_CLIENT) {
      if (this.blockIdx === m.blocks) {
        this.milestonesReached += 1;
        this.scoreEstimate += m.bonus;
        this.cb.onMilestone?.({ blocks: m.blocks, name: m.name, bonus: m.bonus });
        this.targetSky.setHex(m.sky);
        this.targetFog.setHex(m.fog);
        this.audio.setChimeGain(1.0, 450);
        this.chimeSwellRemaining = 3.5;
        // Keep one bright marker chime on the fanfare moment so the swell
        // has a recognizable onset over the soft drone bed.
        this.audio.chime(24, 0);
        break;
      }
    }
    this.cameraTargetY = this.stackHeight - 2;
    this.cameraTargetZ = 28 + Math.min(40, this.stackHeight * 0.45);
    this.bedIntensity = Math.min(1, this.stackHeight / 30);
    this.audio.setDroneIntensity(0.3 + this.bedIntensity * 0.5);

    // Visible gust event on gust-kind blocks AFTER they settle.
    if (kind === "gust") this.fireGust();

    const settleMs = rescued ? 480 : 360;
    // v5(b): if the NEXT block (now blockIdx, post-increment) is a gust,
    // arm a directional arrow ~300ms before it enters. Gust speed is
    // 2.5× normal — players miss them because the block crosses the
    // arena before they read the kind badge. The arrow gives them time
    // to set their tap rhythm against the entry side.
    const next = this.blocks[this.blockIdx];
    if (next && next.kind === "gust") {
      const armDelay = Math.max(0, settleMs - 300);
      setTimeout(() => {
        if (this.gameOver) return;
        this.gustWarnSide = next.side;
        this.gustWarnRemaining = 0.32;
      }, armDelay);
    }

    setTimeout(() => {
      this.settling = false;
      this.blockStartedAt = performance.now();
      this.refreshCurrentMesh();
    }, settleMs);
  }

  private fireGust() {
    this.gustActive = 1;
    this.audio.chime(-5, -0.6);
  }

  private collapseTower() {
    // Animate the stack root tilting steeply over the next ~600ms,
    // then finalize. Cinematic ending instead of a hard cut.
    const start = performance.now();
    const startLean = this.stackRoot.rotation.z;
    const targetLean = (startLean > 0 ? 1 : -1) * 0.9;
    const tick = () => {
      const t = (performance.now() - start) / 600;
      this.stackRoot.rotation.z = startLean + (targetLean - startLean) * ease(Math.min(1, t));
      if (t >= 1) {
        this.finish();
        return;
      }
      requestAnimationFrame(tick);
    };
    tick();
  }

  private frame(dt: number, _t: number) {
    if (this.gameOver) return;

    // v5(a): milestone palette tween — current colors ease toward target
    // at ~1.5/s. Cheap (3 lerps each, no allocation). The held drone
    // swell decays linearly back to 0 over 3.5s; once exhausted, snap
    // setChimeGain to 0 so the next swell can rise cleanly.
    const paletteLerp = Math.min(1, dt * 1.5);
    this.currentSky.lerp(this.targetSky, paletteLerp);
    this.currentFog.lerp(this.targetFog, paletteLerp);
    if (this.engine.scene.background instanceof THREE.Color) {
      this.engine.scene.background.copy(this.currentSky);
    }
    if (this.engine.scene.fog && "color" in this.engine.scene.fog) {
      (this.engine.scene.fog as THREE.FogBase).color.copy(this.currentFog);
    }
    if (this.chimeSwellRemaining > 0) {
      this.chimeSwellRemaining -= dt;
      if (this.chimeSwellRemaining <= 0) {
        this.audio.setChimeGain(0, 600);
        this.chimeSwellRemaining = 0;
      }
    }

    // Camera follow the stack with a smooth damp.
    const lerp = 1 - Math.pow(0.001, dt);
    this.cameraY += (this.cameraTargetY - this.cameraY) * lerp;
    this.cameraZ += (this.cameraTargetZ - this.cameraZ) * lerp * 0.6;
    this.engine.camera.position.set(0, this.cameraY + 3, this.cameraZ);
    this.engine.camera.lookAt(0, this.cameraY - 1.5, 0);

    // Tower lean ramps toward target. Visible sway adds a 2 Hz cosmetic
    // wobble whose amplitude is proportional to cumulative lean.
    const targetLean = this.stack.reduce((acc, b) => acc + b.leanContrib, 0);
    this.leanAngle += (targetLean - this.leanAngle) * lerp * 0.4;
    const swayAmp = Math.min(0.05, Math.abs(this.leanAngle) * 0.5);
    const sway = Math.sin(performance.now() * 0.002) * swayAmp;
    this.stackRoot.rotation.z = this.leanAngle + sway;

    // Animate each stacked block's settle (eases from drop-in to rest).
    for (const b of this.stack) {
      if (b.settle < 1) {
        b.settle = Math.min(1, b.settle + dt * 3.5);
        const dropFromY = b.targetY + 12;
        b.mesh.position.y = dropFromY + (b.targetY - dropFromY) * ease(b.settle);
      }
    }

    // v5(b): gust-warning arrow. Visible for ~320ms during the settle
    // tail of the preceding drop; chevron points inward from the entry
    // side. Vertical position follows the stack top so the cue lives
    // where the player's eye already is. Pulse via a fast sin envelope
    // so it reads as urgent without taking focus from the sweep.
    if (this.gustWarnRemaining > 0 && this.gustWarnSide) {
      this.gustWarnRemaining -= dt;
      const lifeFrac = Math.max(0, Math.min(1, this.gustWarnRemaining / 0.32));
      const pulse = 0.7 + 0.3 * Math.sin(performance.now() * 0.025);
      this.gustArrow.visible = true;
      this.gustArrowMat.opacity = lifeFrac * pulse;
      const dirSign = this.gustWarnSide === "L" ? 1 : -1; // arrow points opposite to entry side
      this.gustArrow.position.set(
        dirSign * -(ARENA_HALF - 2),
        this.stackHeight + 1.6,
        0.6,
      );
      this.gustArrow.rotation.y = dirSign > 0 ? 0 : Math.PI; // flip the chevron
      if (this.gustWarnRemaining <= 0) {
        this.gustArrow.visible = false;
        this.gustWarnSide = null;
      }
    } else if (this.gustArrow.visible) {
      this.gustArrow.visible = false;
    }

    // Gust effect — sweep particles laterally for ~1s.
    if (this.gustActive > 0) {
      this.gustActive = Math.max(0, this.gustActive - dt * 1.2);
      const mat = this.gustParticles.material as THREE.PointsMaterial;
      mat.opacity = this.gustActive * 0.75;
      this.gustParticles.position.x = (1 - this.gustActive) * 30 - 15;
      this.gustParticles.position.y = this.stackHeight - 2;
    } else {
      (this.gustParticles.material as THREE.PointsMaterial).opacity = 0;
    }

    if (this.settling || this.blockIdx >= this.blocks.length) {
      this.currentMesh.position.set(0, this.stackHeight + 60, 0);
      this.ghostBeam.position.set(0, 100, 0);
      return;
    }
    const t = performance.now() - this.blockStartedAt;
    const x = sweepX(this.blocks[this.blockIdx], t);
    this.currentMesh.position.set(x, this.stackHeight + 0.5, 0);

    // Ghost beam highlights the would-be drop. Bright when perfect-zone.
    // Beam color matches the current block's kind so the player reads
    // perfect_only (red) / gust (gold) / magnetic (purple) at a glance.
    const prevCenter = (this.topLeft + this.topRight) / 2;
    const dist = Math.abs(x - prevCenter);
    const curKind = this.blocks[this.blockIdx].kind;
    const tol = curKind === "magnetic" ? PERFECT_TOL_MAGNETIC : PERFECT_TOL;
    const kindBeam = curKind === "perfect_only" ? 0xff3060
      : curKind === "gust" ? 0xffd54a
      : curKind === "magnetic" ? 0x9a72ff
      : 0x46ffe0;
    const ghostMat = this.ghostBeam.material as THREE.MeshBasicMaterial;
    if (this.blockIdx > 0 && dist < tol) {
      ghostMat.opacity = 0.65;
      ghostMat.color.setHex(kindBeam);
      this.currentMat.emissiveIntensity = curKind === "perfect_only" ? 2.4 : curKind === "gust" ? 2.0 : 1.4;
    } else {
      ghostMat.opacity = curKind === "perfect_only" ? 0.35 : 0.18;
      ghostMat.color.setHex(kindBeam);
      this.currentMat.emissiveIntensity = curKind === "perfect_only" ? 1.8 : curKind === "gust" ? 1.6 : curKind === "magnetic" ? 1.2 : 0.7;
    }
    this.ghostBeam.position.set(x, this.stackHeight + 95, 0);

    // Subtle rotation on the current block so it feels alive (no scoring
    // impact — pure visual).
    this.currentMesh.rotation.y = performance.now() * 0.0015;

    const currentKind: BlockKind = this.blockIdx < this.blocks.length ? this.blocks[this.blockIdx].kind : "normal";
    const nextKind: BlockKind = (this.blockIdx + 1) < this.blocks.length ? this.blocks[this.blockIdx + 1].kind : "normal";
    this.cb.onHud({
      height: this.blockIdx,
      perfects: this.perfects,
      scoreEstimate: this.scoreEstimate,
      currentKind,
      nextKind,
      rescueAvailable: !this.rescueUsed,
    });
  }

  private finish() {
    this.audio.setDroneIntensity(0);
    this.audio.setChimeGain(0, 400);
    this.chimeSwellRemaining = 0;
    this.cb.onGameOver({
      drops: this.drops,
      scoreEstimate: this.scoreEstimate,
      perfects: this.perfects,
      height: this.blockIdx,
      rescued: this.rescueUsed,
      milestonesReached: this.milestonesReached,
      kindFaced: { ...this.kindFaced },
      kindPerfect: { ...this.kindPerfect },
    });
  }

  setMuted(b: boolean) { this.audio.setMuted(b); }
  setVolume(v: number) { this.audio.setVolume(v); }
  armAudio() { this.audio.arm(); }

  dispose() {
    this.audio.setChimeGain(0, 0);
    this.canvas.removeEventListener("pointerdown", this.onTap);
    window.removeEventListener("keydown", this.onKey);
    for (const b of this.stack) {
      this.stackRoot.remove(b.mesh);
      b.mesh.geometry.dispose();
      (b.mesh.material as THREE.Material).dispose();
    }
    this.stack = [];
    this.currentGeo.dispose();
    this.currentMat.dispose();
    this.gustArrow.geometry.dispose();
    this.gustArrowMat.dispose();
    this.engine.dispose();
  }
}
